import MobilePageContent from 'app/pages/mobile/mobile-layout/MobilePageContent';
import MobileTransactionItem from './MobileTransactionItem';
import MobileTransactionDetails from './MobileTransactionDetails';
import MobileEditTransactionSheet from './MobileEditTransactionSheet';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MobileTransactionTabs from 'app/pages/mobile/components/MobileTransactionTabs';
import MobileSearchBar from 'app/pages/mobile/components/MobileSearchBar';
import { isReturned } from 'app/pages/overview/tabs/transactions/ReturnedStatusChip';
import { useParams } from 'react-router-dom';
import { useTransactionSheets } from 'app/pages/mobile/hooks/useTransactionSheets';
import { BACKGROUND_SIZES_v2 } from 'app/pages/mobile/utils/constants';
import { useTransactions } from 'app/hooks/useTransactions';
import MobileLoadingOverlay from 'app/pages/mobile/components/MobileLoadingOverlay';
import { useFetchAgaveCommonData } from 'app/hooks/useFetchAgaveCommonData';
import { useAppDispatch, useAppSelector } from 'app/store';
import { applyLocalTransactionUpdates } from 'app/pages/store/transactionsSlice';
import { selectCardsDataSlice } from 'app/pages/store/CardDataSlice/cardDataSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useAgaveSourceSystem } from 'app/hooks/useAgaveSourceSystem';
import { AgaveEmployeeClearedContext } from 'app/pages/overview/tabs/transactions/AgaveTransactions/utils';
import { agaveService } from 'app/services/agave.service';
import { removeTypename } from 'util/helpers';
import { captureException } from '@sentry/react';
import type { AgaveLocation } from 'API';

const MobileTransactions = () => {
  const { t } = useTranslation();
  const { id: deepLinkTransactionId } = useParams<{ id?: string }>();
  const dispatch = useAppDispatch();
  // Fetch Agave account data for Sage 300 client detection and coding
  useFetchAgaveCommonData();
  const { isSageIntacctClient } = useAgaveSourceSystem();
  const { cardDetails } = useAppSelector(selectCardsDataSlice);
  const { selectedPaidolId, selectedPaidol } = useAppSelector(selectUserCompanies);
  const {
    loading,
    loadingMore,
    hadErrorLoading,
    transactions,
    hasMoreTransactions,
    loadTransactions,
    tableFilters,
    nextToken,
  } = useTransactions({ onlyMyTransactions: true });

  const clearedEmployeeIdsRef = useRef<Set<string>>(new Set());
  const registerEmployeeCleared = useCallback((transactionId: string) => {
    clearedEmployeeIdsRef.current.add(transactionId);
  }, []);

  // SEE-944 — same cascade as desktop: when the toggle is on, auto-fill
  // location for the auto-filled employee.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const erpSetting = (selectedPaidol as any)?.erpDepartmentLocationSetting;
  const filterProjectsByEmployeeLocation = Boolean(
    erpSetting?.isEnabled &&
      erpSetting?.locationEnabled &&
      erpSetting?.filterProjectsByEmployeeLocationEnabled
  );
  const lookedUpEmployeeLocationsRef = useRef<Set<string>>(new Set());

  // Auto-fill employee from cardholder default for Sage Intacct (same as web)
  useEffect(() => {
    if (!isSageIntacctClient || !transactions.length) return;
    const updates: Array<{
      id: string;
      changes: { agaveEmployeeId: string; agaveEmployee: { id: string; name: string | null } };
    }> = [];
    const locationLookupByEmployee: Record<string, string[]> = {};
    for (const txn of transactions) {
      if (txn.agaveEmployeeId) continue;
      if (
        (txn.erpApprovalStatus && (txn.agaveExpenseId || txn.erpPostingStatus === 'COMPLETED')) ||
        txn.erpBypassStatus
      )
        continue;
      if (clearedEmployeeIdsRef.current.has(txn.id)) continue;
      const card = cardDetails.get(txn.paymentCardId);
      const defaultId = card?.cardholder?.defaultAgaveEmployeeId;
      const defaultEmp = card?.cardholder?.defaultAgaveEmployee;
      if (defaultId && defaultEmp?.id) {
        updates.push({
          id: txn.id,
          changes: {
            agaveEmployeeId: defaultId,
            agaveEmployee: { id: defaultEmp.id, name: defaultEmp.name ?? null },
          },
        });
        if (filterProjectsByEmployeeLocation && !txn.agaveLocationId) {
          if (!locationLookupByEmployee[defaultId]) locationLookupByEmployee[defaultId] = [];
          locationLookupByEmployee[defaultId].push(txn.id);
        }
      }
    }
    if (updates.length) {
      dispatch(applyLocalTransactionUpdates(updates as Parameters<typeof applyLocalTransactionUpdates>[0]));
    }

    const paidolId = selectedPaidolId;
    if (!paidolId) return;
    for (const [employeeId, txnIds] of Object.entries(locationLookupByEmployee)) {
      const cacheKey = `${paidolId}|${employeeId}`;
      if (lookedUpEmployeeLocationsRef.current.has(cacheKey)) continue;
      lookedUpEmployeeLocationsRef.current.add(cacheKey);
      (async () => {
        try {
          const result = await agaveService.v2SearchAgaveLocations({
            input: { filter: { paidolId, employeeId }, limit: 2 },
          });
          const items = (result?.items ?? []) as AgaveLocation[];
          if (items.length === 1) {
            const cleaned = removeTypename(items[0]) as AgaveLocation;
            const locationUpdates = txnIds.map((id) => ({
              id,
              changes: { agaveLocation: cleaned, agaveLocationId: cleaned.id },
            }));
            dispatch(
              applyLocalTransactionUpdates(
                locationUpdates as Parameters<typeof applyLocalTransactionUpdates>[0]
              )
            );
          }
        } catch (e) {
          captureException(e);
        }
      })();
    }
  }, [
    isSageIntacctClient,
    transactions,
    cardDetails,
    dispatch,
    filterProjectsByEmployeeLocation,
    selectedPaidolId,
  ]);

  const isLoading = !loadingMore && (loading || tableFilters?.filtering);

  // Transactions are already filtered for the current user by useTransactionLoader
  const [tab, setTab] = useState<'all' | 'returned'>('all');
  const [searchValue, setSearchValue] = useState('');
  const displayedTransactions = useMemo(() => {
    let list =
      tab === 'returned'
        ? transactions.filter((transaction) => isReturned(transaction.returnedStatus))
        : transactions;
    const search = searchValue.trim().toLowerCase();
    if (search) {
      list = list.filter((transaction) => {
        const merchantName = transaction.merchantName?.toLowerCase() || '';
        const merchantCategory = transaction.merchantCategory?.toLowerCase() || '';
        const amount = transaction.approvedAmount?.value?.toString() || '';
        const date = transaction.eventDate
          ? new Date(transaction.eventDate).toLocaleDateString().toLowerCase()
          : '';
        return (
          merchantName.includes(search) ||
          merchantCategory.includes(search) ||
          amount.includes(search) ||
          date.includes(search)
        );
      });
    }
    return list;
  }, [tab, transactions, searchValue]);

  const {
    isTransactionDetailsOpen,
    isEditTransactionOpen,
    selectedTransaction,
    handleTransactionClick,
    handleCloseTransactionDetails,
    handleOpenEditTransaction,
    handleCloseEditTransaction,
  } = useTransactionSheets({ transactions: displayedTransactions, defaultSheet: 'details' });

  // Auto-open detail sheet when navigating via deep link (/transactions/:id)
  useEffect(() => {
    if (deepLinkTransactionId && transactions.length > 0) {
      handleTransactionClick(deepLinkTransactionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTransactionId, transactions]);

  const handleLoadMore = useCallback(() => {
    if (nextToken && hasMoreTransactions) {
      loadTransactions(nextToken);
    }
  }, [hasMoreTransactions, loadTransactions, nextToken]);

  return (
    <AgaveEmployeeClearedContext.Provider value={registerEmployeeCleared}>
      {/* Main transaction list */}
      <MobileLoadingOverlay isLoading={isLoading || loadingMore} />
      <MobilePageContent
        transparentHeight={BACKGROUND_SIZES_v2.SHOW_HEADER}
        title="Transactions"
        showCompanySwitcher
      >
        <Stack spacing={2} sx={{ pt: 2 }}>
          <MobileTransactionTabs value={tab} onChange={setTab} />
          <MobileSearchBar value={searchValue} onChange={setSearchValue} />
          {displayedTransactions.length > 0 && (
            <Box sx={{ bgcolor: 'neutral95.main', borderRadius: 2, overflow: 'hidden' }}>
              {displayedTransactions.map((transaction) => (
                <MobileTransactionItem
                  key={transaction.id}
                  transaction={transaction}
                  onTransactionClick={handleTransactionClick}
                />
              ))}
            </Box>
          )}
          {displayedTransactions.length === 0 && !isLoading && !loadingMore && (
            <Typography
              sx={{
                // SEE-1392: a rejected fetch also lands on the zero-rows branch. Show it
                // as an error instead of the reassuring empty copy, which would otherwise
                // tell the user everything is fine when the list never loaded.
                color: hadErrorLoading ? 'error.main' : 'grey.500',
                textAlign: 'center',
                fontStyle: 'italic',
                fontWeight: 300,
                mt: 2,
              }}
            >
              {hadErrorLoading
                ? t('mobileTransactionsLoadError')
                : tab === 'returned'
                ? t('mobileNoReturnedTransactions', 'You have no returned transactions')
                : t('mobileEmptyState')}
            </Typography>
          )}
          {loadingMore && (
            <Stack alignItems="center" py={2}>
              <CircularProgress size={24} />
            </Stack>
          )}
          {!loadingMore && hasMoreTransactions && (
            <Stack alignItems="center" pt={1}>
              <Button size="small" variant="outlined" onClick={handleLoadMore} disabled={loadingMore}>
                Load More
              </Button>
            </Stack>
          )}
        </Stack>
      </MobilePageContent>

      {/* Transaction details */}
      <MobileTransactionDetails
        open={isTransactionDetailsOpen}
        onClose={handleCloseTransactionDetails}
        onEdit={handleOpenEditTransaction}
        transaction={selectedTransaction}
      />

      <MobileEditTransactionSheet
        open={isEditTransactionOpen}
        onClose={handleCloseEditTransaction}
        transaction={selectedTransaction}
      />
    </AgaveEmployeeClearedContext.Provider>
  );
};

export default MobileTransactions;
